import { noddiAsync, URLKeys } from 'noddi-async';
import type { TabItem } from 'noddi-ui-web';
import {
  ApiErrorMessageInternal,
  NavioLocalQueryParamProvider,
  NoddiTable,
  NoddiTabs,
  noddiToast,
  usePagination
} from 'noddi-ui-web';
import { useMemo, useState } from 'react';
import type { UserSessionData } from '../../contexts/AuthContext';
import { useAuthContext } from '../../contexts/useAuthContext';
import { getImpersonationUserColumns } from './getImpersonationUserColumns';
import { ImpersonationConfirmation, type ImpersonationUser } from './ImpersonationConfirmation';
import { ImpersonationOrganizationsTable } from './ImpersonationOrganizationsTable';
import { useImpersonationScopeFilters } from './useImpersonationScopeFilters';
import { useImpersonationUserGroups } from './useImpersonationUserGroups';

type Props = {
  onClose: () => void;
  onImpersonated?: (userData: UserSessionData) => void;
  /** Customer-app brand domain (e.g. `bestdrive`) — pre-selects that brand and scopes users. */
  defaultBrandDomain?: string;
};

/** Its filters live in a local scope: picking one never writes the URL of the page beneath the dialog. */
export const ImpersonateUserDialog = (props: Props) => (
  <NavioLocalQueryParamProvider>
    <ImpersonateUserDialogContent {...props} />
  </NavioLocalQueryParamProvider>
);

const ImpersonateUserDialogContent = ({ onClose, onImpersonated, defaultBrandDomain }: Props) => {
  const [selectedUser, setSelectedUser] = useState<ImpersonationUser | null>(null);
  const [activeTab, setActiveTab] = useState<'users' | 'organizations'>('users');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const { pagination, setPagination } = usePagination();
  const { updateImpersonatedData } = useAuthContext();
  const searchQuery = pagination.search.trim();
  const {
    usersFilters,
    organizationsFilters,
    serviceDepartmentIds,
    userGroupScope,
    isWaitingForBrandScope,
    hasEmptyScope,
    error: scopeError
  } = useImpersonationScopeFilters({ defaultBrandDomain, searchQuery });

  const {
    data: usersPages,
    isPending: isUsersPending,
    isFetching: isUsersFetching,
    isFetchingNextPage: isUsersFetchingNextPage,
    hasNextPage: hasMoreUsers,
    fetchNextPage: fetchNextUsersPage,
    error: usersError
  } = noddiAsync.useInfiniteGet({
    type: URLKeys.getPaginatedAccountsIgnoringImpersonation,
    input: {
      pageSize: pagination.pageSize,
      search: searchQuery || undefined,
      ...usersFilters.apiInput
    },
    queryConfig: {
      // Data-quality filters resolve their matching user groups before this request. Keeping the
      // prior page here would briefly present unfiltered users as matches for the new scope.
      enabled: !isWaitingForBrandScope && !userGroupScope.isPending && !hasEmptyScope
    }
  });

  const users = useMemo(
    () => (hasEmptyScope ? [] : (usersPages?.pages.flatMap((page) => page.results) ?? [])),
    [usersPages, hasEmptyScope]
  );
  const usersTotalCount = hasEmptyScope ? 0 : usersPages?.pages[0]?.count;
  const userIds = useMemo(() => users.map((user) => user.id), [users]);
  const {
    groupsByUserId: userGroupsByUserId,
    isLoading: isUserGroupsLoading,
    isFetching: isUserGroupsFetching,
    error: userGroupsError
  } = useImpersonationUserGroups({
    userIds,
    serviceDepartmentIds: serviceDepartmentIds.length > 0 ? serviceDepartmentIds : undefined
  });
  const columns = useMemo(() => getImpersonationUserColumns(userGroupsByUserId), [userGroupsByUserId]);
  const infiniteScroll = useMemo(
    () => ({
      hasNextPage: !hasEmptyScope && Boolean(hasMoreUsers),
      isFetchingNextPage: isUsersFetchingNextPage,
      fetchNextPage: () => {
        void fetchNextUsersPage();
      }
    }),
    [hasEmptyScope, hasMoreUsers, isUsersFetchingNextPage, fetchNextUsersPage]
  );

  const {
    mutateAsync: impersonateUser,
    isPending,
    error
  } = noddiAsync.usePost({
    type: URLKeys.postImpersonateUserIgnoringImpersonation,
    queryConfig: {
      onSuccess(data) {
        updateImpersonatedData(data.data);
        onImpersonated?.(data.data);
        noddiToast.success('Successfully entered impersonate mode');
        onClose();
      }
    }
  });

  const queryError = scopeError ?? userGroupScope.error ?? usersError ?? userGroupsError;
  if (queryError) {
    return <ApiErrorMessageInternal error={queryError} />;
  }

  if (showConfirmation && selectedUser) {
    return (
      <ImpersonationConfirmation
        user={selectedUser}
        isPending={isPending}
        error={error}
        onCancel={() => {
          setShowConfirmation(false);
          setSelectedUser(null);
        }}
        onConfirm={() => {
          // oxlint-disable-next-line typescript/no-floating-promises -- mutateAsync result unused; the dialog renders error from the same mutation
          impersonateUser({ userId: selectedUser.id });
        }}
      />
    );
  }

  return (
    <div className='flex min-h-0 flex-1 flex-col'>
      <NoddiTabs
        fillHeight
        value={activeTab}
        onValueChange={setActiveTab}
        defaultValue='users'
        tabItems={
          [
            {
              label: 'Accounts',
              value: 'users',
              icon: 'User',
              node: (
                <NoddiTable
                  fillHeight
                  data={users}
                  columns={columns}
                  pagination={pagination}
                  setPagination={setPagination}
                  totalCount={usersTotalCount}
                  isPending={
                    !hasEmptyScope &&
                    (isUsersPending || isUserGroupsLoading || isWaitingForBrandScope || userGroupScope.isPending)
                  }
                  isFetching={
                    !hasEmptyScope &&
                    (isUsersFetching ||
                      isUserGroupsFetching ||
                      isWaitingForBrandScope ||
                      userGroupScope.isFetching ||
                      userGroupScope.isPending)
                  }
                  getRowId={(row) => row.id}
                  searchPlaceholder='Search by name, email or phone number'
                  filters={usersFilters}
                  infiniteScroll={infiniteScroll}
                  disableRowMenu
                  onRowClick={(user) => {
                    setSelectedUser(user);
                    setShowConfirmation(true);
                  }}
                />
              )
            },
            {
              label: 'Organizations',
              value: 'organizations',
              icon: 'Building2',
              node: (
                <ImpersonationOrganizationsTable
                  filters={organizationsFilters}
                  onSelectUser={(user) => {
                    setSelectedUser(user);
                    setShowConfirmation(true);
                  }}
                />
              )
            }
          ] satisfies TabItem<'users' | 'organizations'>[]
        }
      />
    </div>
  );
};
